// 이관 배분 계획(docs/CHARACTER-PRD.md §13.5): 기존 유저 이관을 "계정당 한 번" 정하는 문서. 순수(DOM·저장소·게임 모듈 없음) — Worker도 쓴다.
// 첫 이관 기기가 계획 전체를 서버 character_migrations(계정당 1행)에 먼저 올리고, 다른 기기는 자기 계산 대신 그 계획을 받아 원래 키에서 그대로 적용한다.
// 계획에 담는 것: 만들 캐릭터(영웅·id) · 높은 캐릭터(셀 수 있는 것 전부 받는 쪽) · 장비 개체 uid → 받는 캐릭터 · 낮은 캐릭터에 복사할 진행 기록 · 레벨(판정 근거).
// 해시 = 정렬한 JSON의 FNV 64비트(변경 감지용, 보안 해시 아님). 서버가 같은 함수로 다시 계산해 맞는지 본다.

export type MigrationHero = 'kael' | 'lyra';
/** 낮은 캐릭터에 복사하는 진행 기록(셀 수 있는 것은 0으로 바꿔 복사 — migrate.ts) */
// stage.hell(C10)은 넣지 않는다: 이 표는 배정 해시(이미 이관한 계정의 결정값)에 들어가 바꾸면 옛 배정과 해시가 달라진다.
// 헬 진행은 migrate.ts가 키 단위로 따로 낮춰(owed → claimed) 복사한다
export const PROGRESS_COPY = ['save', 'stage', 'stage.hard', 'daily', 'raid'] as const;

export interface MigrationAllocation {
  v: 1;
  /** 만드는 캐릭터(검사는 늘, 궁수는 라이라 Lv2+일 때 — 첫 기기 판정) */
  heroes: MigrationHero[];
  /** 셀 수 있는 것(골드·재료·장비·소비템·상점 장부·받는 중 보상)을 통째로 받는 캐릭터 */
  high: MigrationHero;
  /** 영웅 → 캐릭터 id */
  ids: Partial<Record<MigrationHero, string>>;
  /** 장비 개체 uid → 받는 영웅(첫 기기 장부 기준). 계획에 없는 개체(다른 기기에만 있던 것)는 같은 규칙(영웅 전용 무기는 그 영웅, 나머지는 high) */
  gear: Record<string, MigrationHero>;
  /** 낮은 캐릭터에 복사하는 진행 기록 */
  copy: readonly string[];
  /** 판정 근거(첫 기기 레벨 — 표시·조사용) */
  levels: Record<MigrationHero, number>;
}

/** 계획 원문 최대 크기(서버 검사) */
export const MAX_ALLOCATION_BYTES = 16 * 1024;

function canonical(v: unknown): string {
  if (Array.isArray(v)) return '[' + v.map(canonical).join(',') + ']';
  if (v !== null && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return '{' + Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => JSON.stringify(k) + ':' + canonical(o[k])).join(',') + '}';
  }
  return JSON.stringify(v);
}

/** 계획 해시(16 hex) */
export function allocationHash(a: MigrationAllocation): string {
  const text = canonical(a);
  let h1 = 0x811c9dc5, h2 = 0x2b5d1a37;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995); h2 ^= h2 >>> 15;
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

const HERO = (v: unknown): v is MigrationHero => v === 'kael' || v === 'lyra';
const ID_RE = /^c_[0-9a-f]{24}$/;

/** 형식 검사(틀리면 null). 서버·클라이언트가 받은 계획을 쓰기 전에 부른다 */
export function parseAllocation(raw: unknown): MigrationAllocation | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1 || !Array.isArray(o.heroes) || !o.heroes.length || o.heroes.length > 2 || !o.heroes.every(HERO) || new Set(o.heroes).size !== o.heroes.length) return null;
  const heroes = o.heroes as MigrationHero[];
  if (!heroes.includes('kael') || !HERO(o.high) || !heroes.includes(o.high)) return null;
  if (o.ids === null || typeof o.ids !== 'object' || Array.isArray(o.ids)) return null;
  const ids: Partial<Record<MigrationHero, string>> = {};
  for (const h of heroes) { const id = (o.ids as Record<string, unknown>)[h]; if (typeof id !== 'string' || !ID_RE.test(id)) return null; ids[h] = id; }
  if (Object.keys(o.ids).some((k) => !heroes.includes(k as MigrationHero))) return null;
  if (o.gear === null || typeof o.gear !== 'object' || Array.isArray(o.gear)) return null;
  const gear: Record<string, MigrationHero> = {};
  for (const [uid, h] of Object.entries(o.gear as Record<string, unknown>)) { if (uid.length > 128 || !HERO(h) || !heroes.includes(h)) return null; gear[uid] = h; }
  if (!Array.isArray(o.copy) || !o.copy.every((c) => typeof c === 'string' && c.length < 32)) return null;
  const lv = o.levels as Record<string, unknown> | null;
  if (!lv || typeof lv !== 'object' || !Number.isInteger(lv.kael) || !Number.isInteger(lv.lyra)) return null;
  const out: MigrationAllocation = { v: 1, heroes, high: o.high, ids, gear, copy: o.copy as string[], levels: { kael: lv.kael as number, lyra: lv.lyra as number } };
  return new TextEncoder().encode(JSON.stringify(out)).length <= MAX_ALLOCATION_BYTES ? out : null;
}
